import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import React, { useEffect, useState } from 'react'
import type { Category } from '@/types/api'

interface BulkEditDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  selectedProducts: string[]
  categories: Category[]
  onBulkEdit: (_data: {
    action: string
    product_ids: string[]
    data?: Record<string, unknown>
  }) => Promise<void>
  isLoading?: boolean
}

export function BulkEditDialog({
  open,
  onOpenChange,
  selectedProducts,
  categories,
  onBulkEdit,
  isLoading = false,
}: BulkEditDialogProps) {
  const [action, setAction] = useState<string>('')
  const [price, setPrice] = useState<string>('')
  const [stock, setStock] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setAction('')
      setPrice('')
      setStock('')
      setCategoryId('')
      setNotes('')
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const data: Record<string, unknown> = {}
    
    switch (action) {
      case 'update_price':
        data.price = parseFloat(price)
        break
      case 'update_stock':
        data.stock_quantity = parseInt(stock, 10)
        break
      case 'update_category':
        data.category_id = categoryId
        break
    }

    await onBulkEdit({
      action,
      product_ids: selectedProducts,
      data: Object.keys(data).length > 0 ? data : undefined,
    })
    
    onOpenChange(false)
  }

  const isFormValid = () => {
    if (!action) return false
    
    switch (action) {
      case 'update_price':
        return price && !isNaN(parseFloat(price)) && parseFloat(price) > 0
      case 'update_stock':
        return stock && !isNaN(parseInt(stock, 10)) && parseInt(stock, 10) >= 0
      case 'update_category':
        return categoryId !== ''
      default:
        return true
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Bulk Edit Products</DialogTitle>
          <DialogDescription>
            Apply changes to {selectedProducts.length} selected product{selectedProducts.length > 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="action" className="text-right">
                Action
              </Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activate">Activate</SelectItem>
                  <SelectItem value="deactivate">Deactivate</SelectItem>
                  <SelectItem value="update_price">Update Price</SelectItem>
                  <SelectItem value="update_stock">Update Stock</SelectItem>
                  <SelectItem value="update_category">Update Category</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {action === 'update_price' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="price" className="text-right">
                  New Price
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="col-span-3"
                  placeholder="Enter new price"
                />
              </div>
            )}

            {action === 'update_stock' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="stock" className="text-right">
                  Stock Quantity
                </Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  className="col-span-3"
                  placeholder="Enter stock quantity"
                />
              </div>
            )}

            {action === 'update_category' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="category" className="text-right">
                  Category
                </Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="notes" className="text-right">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="col-span-3"
                placeholder="Optional notes for this bulk operation"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid() || isLoading}>
              {isLoading ? 'Processing...' : `Apply to ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
